import React, { useContext, useMemo } from "react";
import { type Scores } from "@local-logic/client";
import { useTracking } from "@local-logic/core/tracking";

import { useStateManagerContext } from "~/context/stateManager";
import { useScores } from "~/features/clientLibrary/useScores";
import useEffectOnce from "~/hooks/useEffectOnce";

import {
  NeighborhoodHighlightsProps,
  TrackingPipelineEvents,
  AnalyticsEvents,
} from "./types";
import { includeScores } from "./constants";

export type DefaultContext = {
  isLoading: boolean;
  scores?: Scores["location"];
};

const NeighborhoodHighlightsContext = React.createContext<DefaultContext>(
  {} as DefaultContext,
);
const useNeighborhoodHighlights = () =>
  useContext(NeighborhoodHighlightsContext);

const NeighborhoodHighlightsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { sdkState } = useStateManagerContext<NeighborhoodHighlightsProps>();
  const tracking = useTracking();

  useEffectOnce(() => {
    tracking.postEvent({
      eventType: TrackingPipelineEvents.SDK_VIEWED,
    });

    tracking.trackAnalyticsEvent({
      eventName: AnalyticsEvents.SDK_VIEWED,
    });
  });

  const { data: scoresData, isFetching: isScoresFetching } = useScores({
    location: {
      lat: sdkState.options?.lat,
      lng: sdkState.options?.lng,
    },
    geographyId: sdkState.options?.geographyId,
    include: includeScores,
  });

  const isLoading = useMemo(() => isScoresFetching, [isScoresFetching]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      scores: sdkState.options?.geographyId
        ? scoresData?.geographies[0]?.scores
        : scoresData?.location,
    }),
    [isLoading, scoresData],
  );

  return (
    <NeighborhoodHighlightsContext.Provider value={contextValue}>
      {children}
    </NeighborhoodHighlightsContext.Provider>
  );
};

export { useNeighborhoodHighlights, NeighborhoodHighlightsProvider };
